import { useNavigate } from "@solidjs/router";
import { BigNumber } from "bignumber.js";
import log from "loglevel";
import {
    BiRegularCopy,
    BiRegularDownload,
    BiRegularLock,
    BiRegularLockOpen,
    BiRegularTrash,
} from "solid-icons/bi";
import { IoCheckmark } from "solid-icons/io";
import { Show, createSignal } from "solid-js";

import { copyIconTimeout } from "../../consts/CopyContent";
import { useCreateContext } from "../../context/Create";
import { useGlobalContext } from "../../context/Global";
import { downloadRescueFile } from "../../utils/backup";
import { clipboard } from "../../utils/helper";
import { generateRescueFile } from "../../utils/rescueFile";

const RescueFile = () => {
    const iconSize = 16;
    const navigate = useNavigate();

    const {
        rescueFile,
        t,
        setRescueFile,
        clearSwaps,
        setRescueFileBackupDone,
        setSettingsMenu,
        notify,
        setLastUsedKey,
        clearLastUsedEvmIndex,
        rescueEncryptionEnabled,
        enableRescueEncryption,
        disableRescueEncryption,
    } = useGlobalContext();
    const { setSendAmount, setReceiveAmount } = useCreateContext();

    const [copied, setCopied] = createSignal(false);

    const copy = () => {
        const file = rescueFile();
        if (file === null) {
            return;
        }
        clipboard(file.mnemonic);
        setCopied(true);
        setTimeout(() => setCopied(false), copyIconTimeout);
    };

    const handleReset = async () => {
        const confirmText = window.prompt(t("reset_rescue_key_prompt"));

        if (confirmText === null) {
            // User clicked Cancel
            return;
        }

        if (confirmText.toLowerCase() !== "confirm") {
            alert(t("reset_rescue_key_invalid_confirmation"));
            return;
        }

        try {
            await clearSwaps();
            const newRescueFile = generateRescueFile();
            setRescueFile(newRescueFile);
            setLastUsedKey(0);
            void clearLastUsedEvmIndex();
            setRescueFileBackupDone(false);
            setSettingsMenu(false);
            setSendAmount(BigNumber(0));
            setReceiveAmount(BigNumber(0));
            navigate("/");
            log.info("New Rescue Key generated by user");
            notify("success", t("reset_rescue_key_success"));
        } catch (error) {
            alert(t("reset_rescue_key_error", { error }));
            log.error("Failed to reset rescue key", error);
        }
    };

    const handleEncrypt = async () => {
        const passphrase = window.prompt(t("set_rescue_passphrase_prompt"));
        if (passphrase === null) {
            return;
        }
        if (passphrase.length === 0) {
            alert(t("rescue_passphrase_empty"));
            return;
        }

        const confirmation = window.prompt(
            t("confirm_rescue_passphrase_prompt"),
        );
        if (confirmation === null) {
            return;
        }
        if (confirmation !== passphrase) {
            alert(t("rescue_passphrase_mismatch"));
            return;
        }

        try {
            await enableRescueEncryption(passphrase);
            notify("success", t("encrypt_rescue_key_success"));
        } catch (error) {
            alert(t("encrypt_rescue_key_error", { error }));
            log.error("Failed to encrypt rescue key", error);
        }
    };

    const handleDisableEncryption = () => {
        const confirmText = window.prompt(
            t("disable_rescue_encryption_prompt"),
        );
        if (confirmText === null) {
            return;
        }
        if (confirmText.toLowerCase() !== "confirm") {
            alert(t("reset_rescue_key_invalid_confirmation"));
            return;
        }

        try {
            disableRescueEncryption();
            notify("success", t("disable_rescue_encryption_success"));
        } catch (error) {
            alert(t("encrypt_rescue_key_error", { error }));
            log.error("Failed to disable rescue key encryption", error);
        }
    };

    return (
        <div class="flex" data-testid="rescue-key-download">
            <span class="btn-small" onClick={copy}>
                <Show
                    when={copied()}
                    fallback={<BiRegularCopy size={iconSize} />}>
                    <IoCheckmark size={iconSize} />
                </Show>
            </span>
            &nbsp;
            <span
                class="btn-small"
                onClick={() => downloadRescueFile(rescueFile)}>
                <BiRegularDownload size={iconSize} />
            </span>
            &nbsp;
            <Show
                when={rescueEncryptionEnabled()}
                fallback={
                    <span
                        class="btn-small"
                        data-testid="encrypt-rescue-key"
                        title={t("encrypt_rescue_key")}
                        onClick={() => void handleEncrypt()}>
                        <BiRegularLock size={iconSize} />
                    </span>
                }>
                <span
                    class="btn-small"
                    data-testid="decrypt-rescue-key"
                    title={t("decrypt_rescue_key")}
                    onClick={handleDisableEncryption}>
                    <BiRegularLockOpen size={iconSize} />
                </span>
            </Show>
            &nbsp;
            <span
                class="btn-small btn-danger"
                data-testid="reset-rescue-key"
                onClick={handleReset}>
                <BiRegularTrash size={iconSize} />
            </span>
        </div>
    );
};

export default RescueFile;
